import { useEffect, useState } from 'react';
import { apPaymentApi } from '@/api/apPayment.api';
import { CROSSING_OPTIONS, CROSSING_LABELS } from '@/constants/chequeCrossing';

interface Props {
  supplierId:    string | null;
  /** The paying bank account. The chain ends here, so the answer is per line, not per voucher. */
  bankAccountId: string | null;
  /** What the voucher states, or '' to follow configuration. */
  value:         string;
  onChange:      (crossingType: string) => void;
  canOverride:   boolean;
  disabled:      boolean;
  lineNumber:    number;
}

/**
 * How this cheque will be crossed (ML-09).
 *
 * Shown as text rather than a dropdown, because a crossing is a company decision — configured on
 * the payable account, then on the bank account — and departing from it on one voucher is an
 * exception. A permanently open selector gave an exception the same weight as the cheque number,
 * and worse, showed nothing at all about what would print unless somebody overrode it: the default
 * was invisible on the screen that commits to it.
 *
 * Follows the Cheque Payee Name field, which had the same problem and solved it this way.
 */
export const ChequeCrossingField = ({
  supplierId, bankAccountId, value, onChange, canOverride, disabled, lineNumber,
}: Props) => {
  const [fallback, setFallback] = useState<{ crossingType: string | null; source: string | null } | null>(null);
  const [editing,  setEditing]  = useState(false);

  // Asked of the server, not assembled here: the register applies the same precedence, and a
  // second copy of it would drift into printing something the operator never saw.
  useEffect(() => {
    let cancelled = false;
    if (!supplierId) { setFallback(null); return; }

    apPaymentApi.defaultCrossing(supplierId, bankAccountId)
      .then(res => { if (!cancelled) setFallback(res.data?.data ?? null); })
      .catch(() => { if (!cancelled) setFallback(null); });

    return () => { cancelled = true; };
  }, [supplierId, bankAccountId]);

  const configured  = fallback?.crossingType ?? null;
  const overridden  = value !== '' && value !== configured;
  const effective   = value || configured;
  const label       = effective ? (CROSSING_LABELS[effective] ?? effective) : null;

  const from =
    fallback?.source === 'ACCOUNT' ? 'from the payable account'
    : fallback?.source === 'BANK'  ? 'from the bank account'
    : null;

  if (editing && canOverride && !disabled) {
    return (
      <div className="mt-1">
        <select
          autoFocus
          value={value}
          aria-label={`Cheque crossing line ${lineNumber}`}
          onChange={e => { onChange(e.target.value); setEditing(false); }}
          onBlur={() => setEditing(false)}
          className="w-full px-2 py-1 text-xs rounded border border-slate-300 dark:border-slate-600
                     focus:border-orange-400 bg-white dark:bg-slate-800
                     text-slate-700 dark:text-slate-300"
        >
          <option value="">
            {configured ? `As configured — ${CROSSING_LABELS[configured] ?? configured}` : 'As configured — none'}
          </option>
          {CROSSING_OPTIONS.map(o => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>
    );
  }

  return (
    <p className="mt-1 text-[11px] text-slate-500 dark:text-slate-400" data-testid={`crossing-line-${lineNumber}`}>
      {overridden ? (
        <>
          <span className="text-amber-600 dark:text-amber-400 font-medium">Crossing: {label}</span>
          {' · changed for this voucher'}
          {!disabled && (
            <button type="button" onClick={() => onChange('')}
              className="ml-1 text-brand-600 dark:text-brand-400 hover:underline">
              use default
            </button>
          )}
        </>
      ) : (
        <>
          {/* Said plainly even when nothing is configured: an uncrossed cheque is a decision the
              operator should see before signing it, not discover from the printer. */}
          {label
            ? <>Crossing: <span className="font-medium">{label}</span>{from ? ` ${from}` : ''}</>
            : <>Crossing: none — prints uncrossed</>}
          {canOverride && !disabled && (
            <button type="button" onClick={() => setEditing(true)}
              className="ml-1 text-brand-600 dark:text-brand-400 hover:underline">
              change
            </button>
          )}
        </>
      )}
    </p>
  );
};
